import React from 'react';
import { Activity, Radio, Loader2 } from 'lucide-react';
import useLiveRigFeeds from '@/hooks/useLiveRigFeeds';
import RigTelemetryCard from '@/components/telemetry/RigTelemetryCard';
import FeaturedRigCard from '@/components/telemetry/FeaturedRigCard';
import { Link } from 'react-router-dom';
import { Trophy } from 'lucide-react';

export default function LiveTelemetry() {
  const { rigs, loading, featured, others, liveCount } = useLiveRigFeeds();

  return (
    <div className="min-h-screen bg-background pt-16">
      {/* header */}
      <div className="border-b border-white/5 bg-[#0a0b0e]/80 backdrop-blur-sm sticky top-[var(--header-offset)] z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Activity className="w-6 h-6 text-primary" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full animate-ping" />
            </div>
            <div>
              <h1 className="text-white font-black text-xl md:text-2xl uppercase tracking-tight leading-none">
                Live Rig Telemetry
              </h1>
              <p className="text-white/40 text-xs mt-1">Real-time data from every active Motion Reality rig</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <Stat label="Rigs Live" value={liveCount} accent />
            <Stat label="Total Rigs" value={rigs.length} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 space-y-10">
        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Featured main feeds (Porsche + AMG) */}
            {featured.length > 0 && (
              <section>
                <SectionTitle kicker="MAIN FEEDS" title="Featured Rigs" />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {featured.map((rig) => (
                    <FeaturedRigCard key={rig.id} rig={rig} />
                  ))}
                </div>
              </section>
            )}

            {/* Remaining rigs */}
            <section>
              <SectionTitle kicker="ALL RIGS" title="Live Grid" />
              {others.length === 0 ? (
                <div className="text-center py-16 border border-white/5 bg-[#111115]">
                  <Radio className="w-7 h-7 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">No rigs are broadcasting yet.</p>
                  <p className="text-white/25 text-xs mt-1">Start the telemetry pusher on each rig PC to bring it online.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {others.map((rig) => (
                    <RigTelemetryCard key={rig.id} rig={rig} />
                  ))}
                </div>
              )}
            </section>

            {/* Link to the standalone leaderboard */}
            <section>
              <Link to="/leaderboard?track=nordschleife" className="group relative block border border-[#dd1f26]/30 bg-[#1a0d0e] hover:border-[#dd1f26] hover:shadow-[0_0_15px_rgba(221,31,38,0.2)] transition-all duration-300 px-6 py-5 overflow-hidden">
                <div className="absolute bottom-0 left-0 h-0.5 bg-primary w-0 group-hover:w-full transition-all duration-300" />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-6 h-6 text-[#dd1f26] transition-colors" />
                    <div>
                      <p className="text-[#dd1f26] text-[10px] font-black tracking-[0.3em] mb-1">THE GREEN HELL</p>
                      <p className="text-white/90 group-hover:text-white font-black text-lg uppercase tracking-tight transition-colors">Nordschleife Lap-Time Leaderboard →</p>
                    </div>
                  </div>
                  <p className="text-white/40 group-hover:text-white/70 text-xs hidden sm:block transition-colors">View all-time fastest laps</p>
                </div>
              </Link>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ kicker, title }) {
  return (
    <div className="mb-4">
      <p className="text-primary text-[10px] font-bold tracking-widest mb-1.5">{kicker}</p>
      <h2 className="text-white font-black text-lg uppercase tracking-tight">{title}</h2>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="text-right">
      <p className={`font-black text-2xl tabular-nums leading-none ${accent ? 'text-primary' : 'text-white'}`}>
        {value}
      </p>
      <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mt-1">{label}</p>
    </div>
  );
}